import { Container, DisplayObject, Point } from 'pixi.js';

export type GridObjectType = 'floor' | 'wall' | 'door' | 'window' | 'furniture' | 'decoration';

export interface GridObject {
  id: string;
  type: GridObjectType;
  gridX: number;
  gridY: number;
  floor: number;
  width: number;
  height: number;
  sprite?: DisplayObject;
}

export class GridSystem {
  public static readonly TILE_WIDTH = 64;
  public static readonly TILE_HEIGHT = 32;

  private readonly cells: Map<string, Map<GridObjectType, GridObject>> = new Map();
  private readonly container: Container;

  constructor(container: Container) {
    this.container = container;
  }

  public isOccupied(gridX: number, gridY: number, floor = 0): boolean {
    return this.getObject(gridX, gridY, floor) !== undefined;
  }

  public hasFloor(gridX: number, gridY: number, floor = 0): boolean {
    return this.getObjectByType(gridX, gridY, floor, 'floor') !== undefined;
  }

  public hasFurniture(gridX: number, gridY: number, floor = 0): boolean {
    return this.getObjectByType(gridX, gridY, floor, 'furniture') !== undefined;
  }

  public isAreaAvailable(
    startX: number,
    startY: number,
    width: number,
    height: number,
    floor = 0
  ): boolean {
    for (let x = startX; x < startX + width; x += 1) {
      for (let y = startY; y < startY + height; y += 1) {
        if (this.isOccupied(x, y, floor)) {
          return false;
        }
      }
    }

    return true;
  }

  public getObjectByType(gridX: number, gridY: number, floor: number, type: GridObjectType): GridObject | undefined {
    const key = this.getKey(gridX, gridY, floor);
    const layerMap = this.cells.get(key);
    return layerMap ? layerMap.get(type) : undefined;
  }

  public setObjectByType(gridX: number, gridY: number, floor: number, type: GridObjectType, object: GridObject): void {
    const key = this.getKey(gridX, gridY, floor);
    let layerMap = this.cells.get(key);
    if (!layerMap) {
      layerMap = new Map<GridObjectType, GridObject>();
      this.cells.set(key, layerMap);
    }
    layerMap.set(type, object);
  }

  public removeObjectByType(gridX: number, gridY: number, floor: number, type: GridObjectType): GridObject | undefined {
    const key = this.getKey(gridX, gridY, floor);
    const layerMap = this.cells.get(key);
    if (!layerMap) return undefined;
    
    const object = layerMap.get(type);
    layerMap.delete(type);
    
    // Clean up empty layer maps
    if (layerMap.size === 0) {
      this.cells.delete(key);
    }
    
    return object;
  }

  public setObject(object: GridObject): void {
    this.setObjectByType(object.gridX, object.gridY, object.floor, object.type, object);
  }

  public getObject(gridX: number, gridY: number, floor = 0): GridObject | undefined {
    const key = this.getKey(gridX, gridY, floor);
    const layerMap = this.cells.get(key);
    if (!layerMap) return undefined;
    
    // Return the first object found (for backward compatibility)
    return layerMap.values().next().value;
  }

  public removeObject(gridX: number, gridY: number, floor = 0): GridObject | undefined {
    const key = this.getKey(gridX, gridY, floor);
    const layerMap = this.cells.get(key);
    if (!layerMap) return undefined;
    
    // Remove the first object found (for backward compatibility)
    const firstEntry = layerMap.entries().next();
    if (firstEntry.done) return undefined;
    
    const [type, object] = firstEntry.value;
    layerMap.delete(type);
    
    // Clean up empty layer maps
    if (layerMap.size === 0) {
      this.cells.delete(key);
    }
    
    return object;
  }

  public clear(): void {
    this.cells.clear();
    this.container.removeChildren();
  }

  public getAllObjects(): GridObject[] {
    const objects: GridObject[] = [];
    for (const layerMap of this.cells.values()) {
      for (const object of layerMap.values()) {
        objects.push(object);
      }
    }
    return objects;
  }

  public gridToWorld(gridX: number, gridY: number): Point {
    return new Point(
      (gridX - gridY) * (GridSystem.TILE_WIDTH / 2),
      (gridX + gridY) * (GridSystem.TILE_HEIGHT / 2)
    );
  }

  public worldToGrid(worldX: number, worldY: number): Point {
    const x =
      (worldX / (GridSystem.TILE_WIDTH / 2) +
        worldY / (GridSystem.TILE_HEIGHT / 2)) /
      2;
    const y =
      (worldY / (GridSystem.TILE_HEIGHT / 2) -
        worldX / (GridSystem.TILE_WIDTH / 2)) /
      2;

    return new Point(Math.round(x), Math.round(y));
  }

  private getKey(x: number, y: number, floor: number): string {
    return `${floor}:${x}:${y}`;
  }
}
